/**
 * CRM Ticketing System - Frontend Constants
 * System-wide constants and enumerations
 * Mirrors backend crmConstants.js
 * Created: 2025
 */

export const CRM_CONSTANTS = {
  // Ticket Status Values (must match backend exactly: capitalized with spaces)
  TICKET_STATUS: {
    NEW: { value: 'New', label: 'New', color: '#007bff' },
    IN_PROGRESS: { value: 'In Progress', label: 'In Progress', color: '#ffc107' },
    PENDING: { value: 'Pending', label: 'Pending', color: '#fd7e14' },
    RESOLVED: { value: 'Resolved', label: 'Resolved', color: '#28a745' },
    CLOSED: { value: 'Closed', label: 'Closed', color: '#6c757d' },
    CANCELLED: { value: 'Cancelled', label: 'Cancelled', color: '#dc3545' }
  },

  // Ticket Priority Values (must match backend exactly: capitalized)
  TICKET_PRIORITY: {
    LOW: { value: 'Low', label: 'Low', color: '#28a745' },
    NORMAL: { value: 'Normal', label: 'Normal', color: '#007bff' },
    HIGH: { value: 'High', label: 'High', color: '#fd7e14' },
    URGENT: { value: 'Urgent', label: 'Urgent', color: '#dc3545' }
  },

  // Ticket Source Types (must match backend exactly: capitalized)
  TICKET_SOURCE: {
    WEB_FORM: { value: 'Web Form', label: 'Web Form' },
    EMAIL: { value: 'Email', label: 'Email' },
    PHONE: { value: 'Phone', label: 'Phone' },
    CHAT: { value: 'Chat', label: 'Chat' }
  },

  // Team Codes
  TEAM_CODES: {
    HR: 'HR',
    TA: 'TA',
    LD: 'LD',
    CB: 'CB'
  },

  // SLA Status Types
  SLA_STATUS: {
    ON_TRACK: 'ON TRACK',
    AT_RISK: 'AT RISK',
    BREACHED: 'BREACHED'
  },

  // Error Messages
  ERROR_MESSAGES: {
    TICKET_NOT_FOUND: 'Ticket not found',
    INVALID_STATUS: 'Invalid ticket status',
    INVALID_PRIORITY: 'Invalid ticket priority',
    STORE_NOT_FOUND: 'Store not found',
    TEAM_NOT_FOUND: 'Team not found',
    CATEGORY_NOT_FOUND: 'Category not found',
    UNAUTHORIZED: 'Unauthorized access',
    VALIDATION_FAILED: 'Validation failed',
    NETWORK_ERROR: 'Network error occurred'
  },

  // Success Messages
  SUCCESS_MESSAGES: {
    TICKET_CREATED: 'Ticket created successfully',
    TICKET_UPDATED: 'Ticket updated successfully',
    TICKET_ASSIGNED: 'Ticket assigned successfully',
    TICKET_RESOLVED: 'Ticket resolved successfully',
    TICKET_CLOSED: 'Ticket closed successfully',
    COMMENT_ADDED: 'Comment added successfully',
    NOTE_SAVED: 'Note saved successfully',
    REPLY_SENT: 'Reply sent successfully'
  },

  // Pagination
  PAGINATION: {
    DEFAULT_PAGE_SIZE: 25,
    MAX_PAGE_SIZE: 100,
    DEFAULT_PAGE: 1,
    ITEMS_PER_PAGE_OPTIONS: [10, 25, 50, 100]
  },

  // Field Lengths (matching backend and database schema)
  FIELD_LENGTHS: {
    TICKET_NUMBER: 20,
    EMAIL: 255,
    NAME: 255,
    EMPLOYEE_ID: 50,
    PHONE: 50,
    STORE_PC: 20,
    STORE_NAME: 200,
    MARKET: 100,
    TITLE: 500,
    PRIORITY: 20,
    STATUS: 50,
    SOURCE: 50,
    DESCRIPTION: 5000,
    NOTE: 2000
  },

  // UI Constants
  UI: {
    TICKET_LOAD_LIMIT: 100,
    AUTO_SAVE_DELAY: 1000, // milliseconds
    SUCCESS_MESSAGE_DURATION: 3000, // milliseconds
    SIDEBAR_WIDTH: 300, // pixels
    TICKET_CARD_PREVIEW_LINES: 2
  },

  // Date Filter Options
  DATE_FILTERS: {
    ALL: { value: 'all', label: 'All Time' },
    SEVEN_DAYS: { value: '7days', label: 'Last 7 days' },
    THIRTY_DAYS: { value: '30days', label: 'Last 30 days' }
  }
};

// Helper functions to get status/priority arrays for dropdowns
export const getStatusOptions = () => {
  return Object.values(CRM_CONSTANTS.TICKET_STATUS);
};

export const getPriorityOptions = () => {
  return Object.values(CRM_CONSTANTS.TICKET_PRIORITY);
};

export const getSourceOptions = () => {
  return Object.values(CRM_CONSTANTS.TICKET_SOURCE);
};

export const getDateFilterOptions = () => {
  return Object.values(CRM_CONSTANTS.DATE_FILTERS);
};

// Helper function to get status color
export const getStatusColor = (statusValue) => {
  const status = Object.values(CRM_CONSTANTS.TICKET_STATUS).find(
    s => s.value === statusValue || s.label.toLowerCase() === statusValue?.toLowerCase()
  );
  return status?.color || '#6c757d';
};

// Helper function to get priority color
export const getPriorityColor = (priorityValue) => {
  const priority = Object.values(CRM_CONSTANTS.TICKET_PRIORITY).find(
    p => p.value === priorityValue || p.label.toLowerCase() === priorityValue?.toLowerCase()
  );
  return priority?.color || '#6c757d';
};

// Helper function to get status label
export const getStatusLabel = (statusValue) => {
  const status = Object.values(CRM_CONSTANTS.TICKET_STATUS).find(
    s => s.value === statusValue
  );
  return status?.label || statusValue;
};

// Helper function to get priority label
export const getPriorityLabel = (priorityValue) => {
  const priority = Object.values(CRM_CONSTANTS.TICKET_PRIORITY).find(
    p => p.value === priorityValue
  );
  return priority?.label || priorityValue;
};

// Helper function to calculate due date (created_at + 24 hours)
export const calculateDueDate = (createdAt) => {
  if (!createdAt) return null;
  const date = new Date(createdAt);
  date.setHours(date.getHours() + 24);
  return date;
};

// Helper function to format date in friendly format: "MM/DD/YYYY HH:MM am/pm"
export const formatFriendlyDate = (date) => {
  if (!date) return '';

  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();

  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12;
  hours = hours ? hours : 12; // 0 should be 12
  const hoursStr = String(hours).padStart(2, '0');

  return `${month}/${day}/${year} ${hoursStr}:${minutes} ${ampm}`;
};

export default CRM_CONSTANTS;
